import { useMemo, useState } from 'react';
import { format, addDays, startOfDay } from 'date-fns';
import clsx from 'clsx';
import { Wallet, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api/queryClient';
import { useAvailabilityView } from '@/api/views';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { buildHeatmap, type HeatmapCell } from '@/domain/queueEngine';
import { hourToIso } from '@/domain/depositWindow';
import { artistMatchesStyle } from '@/domain/availabilityScope';
import { simTimeFull, simTimeShort } from '@/utils/format';
import ArtistAvailabilityHeatmap from '@/components/queue/ArtistAvailabilityHeatmap';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import DepositLinkModal from '@/components/DepositLinkModal';
import type { CustomerRecord } from '@/domain/types';

type ArtistScope = 'best' | 'all';

interface Props {
  customer: CustomerRecord;
}

/**
 * The availability + grounded-deposit pane, standalone so any surface (the
 * queue work modal's sibling, the Conversations detail) can drop it in: pick a
 * heatmap cell, pick a free window, then Send deposit grounded to that slot.
 * Mirrors the My Queue right rail behaviour without its gap-fill specifics.
 */
function SlotDepositPaneInner({ customer }: Props) {
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const [weekStartIso, setWeekStartIso] = useState(() => startOfDay(new Date(clockIso)).toISOString());
  const [scope, setScope] = useState<ArtistScope>('best');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [updatedIso, setUpdatedIso] = useState(clockIso);
  const [selection, setSelection] = useState<{ artistId: string; dayIso: string } | null>(null);
  const [pinnedSlot, setPinnedSlot] = useState<{
    artistId: string;
    startIso: string;
    endIso: string;
  } | null>(null);
  const [depositOpen, setDepositOpen] = useState(false);

  const studioForArtists = customer.preferredStudioId ?? customer.primaryStudioId;

  // Live-read availability for the heatmap's one-week window (tv-1vh.10).
  const weekEndIso = useMemo(() => addDays(new Date(weekStartIso), 7).toISOString(), [weekStartIso]);
  const { data: availability, refetch: refetchAvailability } = useAvailabilityView(
    studioForArtists,
    weekStartIso,
    weekEndIso,
  );
  const bookings = availability?.bookings ?? {};

  const heatmapRows = useMemo(
    () =>
      buildHeatmap({
        artists,
        bookings,
        nowIso: clockIso,
        studioIds: [studioForArtists],
        startIso: weekStartIso,
        days: 7,
      }),
    // refreshNonce is an intentional recompute trigger (re-reads live store state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artists, bookings, clockIso, studioForArtists, weekStartIso, refreshNonce],
  );

  const preferredStyle = customer.preferredStyle;
  const preferredArtistId = customer.preferredArtistId;
  const bestFitFallsBackToAll = !preferredStyle && !preferredArtistId;

  const bestFitRows = useMemo(() => {
    const ids = new Set<string>();
    for (const row of heatmapRows) {
      if (preferredStyle && artistMatchesStyle(row.artist, preferredStyle)) ids.add(row.artist.id);
    }
    if (preferredArtistId) ids.add(preferredArtistId);
    return heatmapRows.filter((r) => ids.has(r.artist.id));
  }, [heatmapRows, preferredStyle, preferredArtistId]);

  const scopedRows =
    scope === 'all' || bestFitFallsBackToAll || (scope === 'best' && bestFitRows.length === 0)
      ? heatmapRows
      : bestFitRows;

  const studioLabel = studios[studioForArtists]?.name ?? studioForArtists;
  const scopeNote =
    scope === 'all'
      ? `All artists at ${studioLabel}.`
      : bestFitFallsBackToAll
        ? `Best fit - no style or artist preference yet, showing all artists at ${studioLabel}.`
        : `Best fit - ${
            preferredStyle ? `${preferredStyle.replace('-', ' ')} artists` : 'the preferred artist'
          } at ${studioLabel}.`;

  const weekStart = new Date(weekStartIso);
  const weekRangeLabel = `${format(weekStart, 'd MMM')} - ${format(addDays(weekStart, 6), 'd MMM')}`;
  const stepWeek = (deltaWeeks: number) =>
    setWeekStartIso(addDays(new Date(weekStartIso), deltaWeeks * 7).toISOString());
  const onRefresh = () => {
    setRefreshNonce((n) => n + 1);
    setUpdatedIso(clockIso);
    void refetchAvailability();
  };

  const onSelectCell = (cell: HeatmapCell) => setSelection({ artistId: cell.artistId, dayIso: cell.dayIso });
  const onPickWindow = (w: { startHour: number; endHour: number }) => {
    if (!selection) return;
    setPinnedSlot({
      artistId: selection.artistId,
      startIso: hourToIso(selection.dayIso, w.startHour),
      endIso: hourToIso(selection.dayIso, w.endHour),
    });
  };

  const pinnedArtist = pinnedSlot ? artists[pinnedSlot.artistId] : undefined;

  return (
    <div className="space-y-4" data-testid="slot-deposit-pane">
      {/* Pin the week-advance + scope controls: the pane scrolls inside a short
          right-rail, so without this the ◀ ▶ week nav is pushed above the top
          edge and clipped the moment a slot/day is picked (tv-6qv.45). */}
      <div className="space-y-2 sticky top-0 z-10 bg-ink-deep pb-2 -mt-1 pt-1">
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex items-center rounded-md border border-gold-dim/30 overflow-hidden">
            <button
              type="button"
              onClick={() => stepWeek(-1)}
              className="px-2 py-1 text-cream-soft hover:text-gold-bright transition"
              aria-label="Previous week"
              data-testid="pane-week-prev"
            >
              <ChevronLeft size={12} />
            </button>
            <span
              className="px-3 py-1 text-[10px] font-inter uppercase tracking-widest text-cream whitespace-nowrap"
              data-testid="pane-week-range"
            >
              {weekRangeLabel}
            </span>
            <button
              type="button"
              onClick={() => stepWeek(1)}
              className="px-2 py-1 text-cream-soft hover:text-gold-bright transition"
              aria-label="Next week"
              data-testid="pane-week-next"
            >
              <ChevronRight size={12} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center rounded-md border border-gold-dim/30 overflow-hidden">
              {(['best', 'all'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={clsx(
                    'px-2 py-1 text-[10px] font-inter uppercase tracking-widest transition',
                    scope === s ? 'bg-gold/15 text-gold-bright' : 'text-cream-soft hover:text-gold-bright',
                  )}
                  data-testid={`pane-scope-${s}`}
                >
                  {s === 'best' ? 'Best fit' : 'All'}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onRefresh}
              className="btn-quiet text-[10px] shrink-0"
              data-testid="pane-refresh"
              title="Re-check availability against current bookings"
              aria-label="Refresh availability"
            >
              <RefreshCw size={11} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="font-inter text-[10px] text-cream-faint leading-relaxed">{scopeNote}</p>
          <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest whitespace-nowrap">
            Updated {simTimeShort(updatedIso)}
          </p>
        </div>
      </div>

      <ArtistAvailabilityHeatmap
        rows={scopedRows}
        studios={studios}
        selected={selection}
        onSelect={onSelectCell}
        onClear={() => setSelection(null)}
      />

      {selection ? (
        <ArtistDaySchedule
          artistId={selection.artistId}
          dayIso={selection.dayIso}
          onPickWindow={onPickWindow}
        />
      ) : null}

      {pinnedSlot ? (
        <div
          className="rounded-lg border border-gold-bright/50 bg-gold/10 p-3"
          data-testid="pane-pinned-slot"
        >
          <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright">
            Discussed slot · grounding
          </p>
          <p className="font-serif text-sm text-cream mt-1">
            {pinnedArtist?.displayName ?? pinnedSlot.artistId} · {simTimeFull(pinnedSlot.startIso)} –{' '}
            {simTimeShort(pinnedSlot.endIso)}
          </p>
          <button
            type="button"
            onClick={() => setDepositOpen(true)}
            className="btn-primary w-full mt-3 justify-center"
            data-testid="pane-send-deposit"
          >
            <Wallet size={13} /> Send deposit · grounded to this slot
          </button>
        </div>
      ) : null}

      <DepositLinkModal
        open={depositOpen}
        customer={customer}
        onClose={() => setDepositOpen(false)}
        discussedSlot={pinnedSlot ?? undefined}
        initialPieceName={customer.designIntent?.notes}
      />
    </div>
  );
}

/**
 * Guarantees a QueryClient for `useAvailabilityView` even when this pane
 * mounts standalone (existing unit tests render it directly, without the app
 * root's `<QueryClientProvider>` from main.tsx). Nesting the same shared
 * `queryClient` singleton here is a no-op when an ancestor already supplies
 * it — same client instance, so no duplicate cache.
 */
export default function SlotDepositPane(props: Props) {
  return (
    <QueryClientProvider client={queryClient}>
      <SlotDepositPaneInner {...props} />
    </QueryClientProvider>
  );
}
